import {
    CheckoutErrorName,
    CheckoutLifecycleEventPostMessageName,
    checkoutPostMessage,
} from '@eventbrite/checkout-postmessage-api';
import logger from '@eventbrite/client-logger';
import { gettext } from '@eventbrite/i18n';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';
import { applePay, ApplePay, client as braintreeClient } from 'braintree-web';
import { ApplePayPaymentRequest } from 'braintree-web/modules/apple-pay';
import { Dispatch } from 'redux';
import { change, stopSubmit, submit } from 'redux-form';
import {
    CHECKOUT_FORM_NAME,
    TICKETS_FULL_SIZE_PATH,
    TICKETS_PAGE_FORM_NAME,
} from '../constants';
import { ERROR_APPLE_PAY_CONFIG } from '../constants/expressCheckout';
import { getWidgetParentUrl } from '../selectors/app';
import {
    getApplePayInstance,
    getCanMakePaymentsWithActiveCard,
    getIsApplePaySupported,
    isExpressApplePayEnabled,
    isTLDWithApplePayEnabled,
} from '../selectors/applePay';
import { getTotalAmount } from '../selectors/order';
import { isOnlyCheckoutPage, isTicketsCompactPage } from '../selectors/routing';
import { CheckoutState } from '../state';
import { AppNamespace } from '../state/app';
import { EventNamespace } from '../state/event';
import { OrderNamespace } from '../state/order';
import {
    browserSupportsApplePay,
    getApplePayBreakdownItems,
    getApplePayBuyerData,
    isApplePayCardActive,
} from '../utils/applePay';
import { clearNonceInForm } from '../utils/expressCheckout';
import {
    trackApplePayCanceled,
    trackApplePayConfirmed,
    trackApplePayShowed,
    trackExpressCheckoutShowed,
} from './eventAnalytics';
import {
    notifyParentExpressPaymentCanceled,
    notifyParentExpressPaymentProcessingOrder,
    notifyParentOrderPlacingError,
} from './notifyParent';
import { getAndSetBraintreeToken } from './order/getAndSetBraintreeToken';

/**
 * We need this interface since the types of braintree-web don't include it, but it's there.
 */
export interface ApplePayWithMerchantIdentifier extends ApplePay {
    merchantIdentifier: string;
}

declare global {
    interface Window {
        ApplePaySession: typeof ApplePaySession;
    }
}

export enum ApplePayActionType {
    setApplepayPaymentRequest = 'setApplepayPaymentRequest',
    setApplepayInstance = 'setApplepayInstance',
    setCanMakePaymentsWithActiveCard = 'setCanMakePaymentsWithActiveCard',
    setHasAsyncErrors = 'setHasAsyncErrors',
    setIsApplePaySupported = 'setIsApplePaySupported',
    setIsMakingApplePayPayment = 'setIsMakingApplePayPayment',
}

export interface SetApplePayPaymentRequestAction {
    type: ApplePayActionType.setApplepayPaymentRequest;
    payload: {
        applepayPaymentRequest: ApplePayPaymentRequest | null;
    };
}

export interface SetApplePayInstanceAction {
    type: ApplePayActionType.setApplepayInstance;
    payload: {
        applepayInstance: ApplePay;
    };
}

export interface SetCanMakePaymentsWithActiveCardAction {
    type: ApplePayActionType.setCanMakePaymentsWithActiveCard;
    payload: {
        canMakePaymentsWithActiveCard: boolean;
    };
}

export interface SetHasAsyncErrorsAction {
    type: ApplePayActionType.setHasAsyncErrors;
    payload: {
        hasAsyncErrors: boolean;
    };
}

export interface SetIsApplePaySupportedAction {
    type: ApplePayActionType.setIsApplePaySupported;
    payload: {
        isApplePaySupported: boolean;
    };
}

export interface setIsMakingApplePayPaymentAction {
    type: ApplePayActionType.setIsMakingApplePayPayment;
    payload: {
        isMakingApplePayPayment: boolean;
    };
}

export const setApplepayPaymentRequest = (
    applepayPaymentRequest: ApplePayPaymentRequest | null,
): SetApplePayPaymentRequestAction => ({
    type: ApplePayActionType.setApplepayPaymentRequest,
    payload: {
        applepayPaymentRequest,
    },
});

export const setApplepayInstance = (
    applepayInstance: ApplePay,
): SetApplePayInstanceAction => ({
    type: ApplePayActionType.setApplepayInstance,
    payload: {
        applepayInstance,
    },
});

export const setCanMakePaymentsWithActiveCard = (
    canMakePaymentsWithActiveCard: boolean,
): SetCanMakePaymentsWithActiveCardAction => ({
    type: ApplePayActionType.setCanMakePaymentsWithActiveCard,
    payload: {
        canMakePaymentsWithActiveCard,
    },
});

export const setIsMakingApplePayPayment = (
    isMakingApplePayPayment: boolean,
): setIsMakingApplePayPaymentAction => ({
    type: ApplePayActionType.setIsMakingApplePayPayment,
    payload: {
        isMakingApplePayPayment,
    },
});

export const setHasAsyncErrors = (
    hasAsyncErrors: boolean,
): SetHasAsyncErrorsAction => ({
    type: ApplePayActionType.setHasAsyncErrors,
    payload: {
        hasAsyncErrors,
    },
});

const getApplePayPaymentRequest = async (
    state: CheckoutState,
    applepayInstance: ApplePay,
): Promise<ApplePayPaymentRequest | null> => {
    const { event, app } = state as {
        order: OrderNamespace;
        event: EventNamespace;
        app: AppNamespace;
    };

    const totalAmount = getTotalAmount(state);

    if (!totalAmount) {
        return null;
    }
    const lineItems = getApplePayBreakdownItems(
        state as CheckoutState & {
            order: OrderNamespace;
            event: EventNamespace;
        },
    );
    const payRequest = {
        total: {
            label: gettext('Tickets for %(eventName)s', {
                eventName: event.name.text,
            }).toString(),
            amount: `${totalAmount}`,
        },
        currencyCode: `${event.currency}`,
        merchantCapabilities: [
            'supports3DS',
            'supportsDebit',
            'supportsCredit',
        ],
        supportedNetworks: ['masterCard', 'visa', 'amex', 'discover'],
        countryCode: `${app.defaultUserCountry}`,
        requiredShippingContactFields: ['name', 'email'],
        lineItems,
    };

    return applepayInstance?.createPaymentRequest(payRequest);
};

export const setIsApplePaySupported =
    () =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const isSupported =
            isTLDWithApplePayEnabled(getState()) && browserSupportsApplePay();

        dispatch({
            type: ApplePayActionType.setIsApplePaySupported,
            payload: {
                isApplePaySupported: isSupported,
            },
        });
    };

export const createApplePayInstance = async (order: OrderNamespace) => {
    const braintreeToken = order.braintreeClientToken;
    const client = await braintreeClient.create({
        authorization: braintreeToken,
    });
    return applePay.create({
        client,
    }) as Promise<ApplePayWithMerchantIdentifier>;
};

export const initializeApplePay =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        try {
            await dispatch(getAndSetBraintreeToken());

            const applepayInstance = await createApplePayInstance(
                getState().order as OrderNamespace,
            );
            const canMakePaymentsWithActiveCard =
                getIsApplePaySupported(getState()) &&
                (await isApplePayCardActive(applepayInstance));

            dispatch(setApplepayInstance(applepayInstance));
            dispatch(
                setCanMakePaymentsWithActiveCard(canMakePaymentsWithActiveCard),
            );
        } catch (error) {
            dispatch(setHasAsyncErrors(true));
            logger.error(error);
        }
    };

export const createApplePayPaymentRequest =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        try {
            const state = getState();
            const applepayPaymentRequest = await getApplePayPaymentRequest(
                state,
                getApplePayInstance(state) as ApplePayWithMerchantIdentifier,
            );

            dispatch(setApplepayPaymentRequest(applepayPaymentRequest));
        } catch (error) {
            logger.error(error);
        }
    };

export const initializeApplePayIfExpressCheckout =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        if (isExpressApplePayEnabled(getState())) {
            dispatch(initializeApplePay());
            dispatch(
                trackApplePayShowed(
                    getCanMakePaymentsWithActiveCard(getState()),
                ),
            );
            dispatch(trackExpressCheckoutShowed(TICKETS_FULL_SIZE_PATH));
        }
    };

export const createApplePayPaymentRequestIfExpressCheckout =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        if (isExpressApplePayEnabled(getState())) {
            dispatch(createApplePayPaymentRequest());
        }
    };

export const onValidateMerchantHandler =
    (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
        session: ApplePaySession,
    ) =>
    (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
        const { applepay } = getState();

        (applepay.applepayInstance as ApplePay).performValidation(
            {
                validationURL: event.validationURL,
                displayName: 'eventbrite',
            },
            (err, validationData) => {
                if (err) {
                    dispatch(setIsMakingApplePayPayment(false));
                    logger.error(err);
                    session.abort();
                    throw err;
                }
                session.completeMerchantValidation(validationData);
            },
        );
    };

export const onPaymentAuthorizedHandler =
    (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
        session: ApplePaySession,
        isTicketSelectionPage?: boolean,
    ) =>
    (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
        const { applepay } = getState();

        (applepay.applepayInstance as ApplePay).tokenize(
            {
                token: event.payment.token,
            },
            (tokenizeErr, payload) => {
                if (tokenizeErr) {
                    dispatch(setIsMakingApplePayPayment(false));
                    logger.error(`Error tokenizing Apple Pay: ${tokenizeErr}`);
                    throw tokenizeErr;
                }

                clearNonceInForm(dispatch);

                dispatch(
                    change(
                        CHECKOUT_FORM_NAME,
                        'payment.applePayBraintreeNonce',
                        payload?.nonce,
                    ),
                );

                dispatch(
                    trackApplePayConfirmed(
                        applepay.canMakePaymentsWithActiveCard,
                    ),
                );

                session.completePayment(ApplePaySession.STATUS_SUCCESS);

                if (isTicketSelectionPage) {
                    dispatch(
                        change(
                            TICKETS_PAGE_FORM_NAME,
                            'payment.applePayBraintreeNonce',
                            payload?.nonce,
                        ),
                    );

                    dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            'buyer',
                            getApplePayBuyerData(
                                dispatch,
                                event,
                                getState().user,
                            ),
                        ),
                    );

                    return dispatch(submit(TICKETS_PAGE_FORM_NAME));
                }

                return dispatch(submit(CHECKOUT_FORM_NAME));
            },
        );
    };

export const onCancelHandler =
    (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
        session: ApplePaySession,
    ) =>
    () => {
        const { applepay } = getState();
        const widgetParentUrl = getWidgetParentUrl(getState());

        dispatch(trackApplePayCanceled(applepay.canMakePaymentsWithActiveCard));
        dispatch(setIsMakingApplePayPayment(false));
        dispatch(stopSubmit(TICKETS_PAGE_FORM_NAME));
        checkoutPostMessage({
            widgetParentUrl,
            messageName:
                CheckoutLifecycleEventPostMessageName.applePayPaymentCanceled,
        });

        if (
            isOnlyCheckoutPage(getState()) ||
            isTicketsCompactPage(getState())
        ) {
            dispatch(notifyParentExpressPaymentCanceled());
        }

        session.completePayment(ApplePaySession.STATUS_FAILURE);
    };

export const startApplePaySession =
    (isTicketSelectionPage?: boolean) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const { applepay } = getState();

        if (
            !window.ApplePaySession ||
            !applepay.applepayInstance ||
            !applepay.applepayPaymentRequest
        ) {
            return;
        }

        try {
            dispatch(notifyParentExpressPaymentProcessingOrder());
            dispatch(setIsMakingApplePayPayment(true));

            const session = new window.ApplePaySession(
                3,
                applepay.applepayPaymentRequest as ApplePayJS.ApplePayPaymentRequest,
            );

            session.onvalidatemerchant = onValidateMerchantHandler(
                dispatch,
                getState,
                session,
            );

            session.onpaymentauthorized = onPaymentAuthorizedHandler(
                dispatch,
                getState,
                session,
                isTicketSelectionPage,
            );

            session.oncancel = onCancelHandler(dispatch, getState, session);

            session.begin();
        } catch (error) {
            dispatch(setIsMakingApplePayPayment(false));
            dispatch(stopSubmit(TICKETS_PAGE_FORM_NAME));
            logger.error(error);
            dispatch(
                notifyParentOrderPlacingError(
                    CheckoutErrorName.ERROR_APPLE_PAY_CONFIG,
                ),
            );
            throw new CustomSubmissionError(
                {
                    _error: ERROR_APPLE_PAY_CONFIG,
                },
                {},
            );
        }
    };

export type ApplePayAction =
    | SetApplePayPaymentRequestAction
    | SetApplePayInstanceAction
    | SetCanMakePaymentsWithActiveCardAction
    | SetHasAsyncErrorsAction
    | SetIsApplePaySupportedAction
    | setIsMakingApplePayPaymentAction;
